import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';

import {
  CarouselProducts,
  CarouselProductType,
} from 'components/Common/Carousel/CarouselProducts';
import { ErrorBoundary } from 'components/Common/Errors/ErrorBoundary/ErrorBoundary';
import Footer from 'components/Common/Footer/Footer';
import useEnv from 'hooks/useEnv';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import { fetcher } from 'services/fetcher';
import {
  getFormattedFlowAttributeDefinition,
  getFormattedMetaData,
  getFormattedProductDetailToCarouselProduct,
  replaceByLinkEnv,
} from 'services/formatter';
import { getFullUri } from 'services/generic';
import { getMerchActionProducts, MerchActions } from 'services/merchandising';
import { DESKTOP_TABLET_MIN } from 'settings/device';
import {
  MAX_PRODUCTS_HPPush1Produit,
  MAX_PRODUCTS_HPPush2Produit,
} from 'settings/merchandising';
import { GTM_PAGE_HOME } from 'settings/gtm/gtmPageView';
import { Homepage, merchHtmlContent } from 'types/Homepage';
import { ProductDetail } from 'types/Controls/ProductSheet';
import { FormattedFlowAttributeType } from 'types/formatter';

import styles from './HomePage.module.scss';

/**
 * Homepage of website. Contains all homepage merch actions (products and html content). <br />
 * Use API CALL Homepage.cxml
 */
export default function HomePage(): JSX.Element {
  const { datetimeApp } = useEnv();
  const { t } = useTranslation();
  const { data, error, isValidating } = useSWRImmutable<Homepage>(
    `${process.env.REACT_APP_URL_API_CATALOG}/json/Custom/Homepage.cxml?${datetimeApp}`,
    fetcher
  );

  const attributes = getFormattedFlowAttributeDefinition(
    data?.Header?.Controls[1]?.Attributes
  );

  const metaData = getFormattedMetaData(
    data?.Zones.find(element => {
      return element.Name === 'Page';
    })?.Controls[0]?.Hierarchy?.Metadata
  );

  const pageTitle = metaData?.Title?.Value || t('DEFAULT_HEAD_TITLE');

  useGtmPageView(GTM_PAGE_HOME, pageTitle, {
    isLoaded: (!!data || !!error) && !isValidating,
  });

  const HPEncart1BanniereContent = (data?.Zones.find(element => {
    return element.Name === 'HP_Encart1Banniere';
  })?.Controls[0]?.Actions[0] as merchHtmlContent)?.HtmlContent;
  const HPEncart2BanniereContent = (data?.Zones.find(element => {
    return element.Name === 'HP_Encart2Banniere';
  })?.Controls[0]?.Actions[0] as merchHtmlContent)?.HtmlContent;
  const HPEncart3BanniereContent = (data?.Zones.find(element => {
    return element.Name === 'HP_Encart3Banniere';
  })?.Controls[0]?.Actions[0] as merchHtmlContent)?.HtmlContent;
  const HPEncart4BanniereContent = (data?.Zones.find(element => {
    return element.Name === 'HP_Encart4Banniere';
  })?.Controls[0]?.Actions[0] as merchHtmlContent)?.HtmlContent;
  const HPEncart5BanniereContent = (data?.Zones.find(element => {
    return element.Name === 'HP_Encart5Banniere';
  })?.Controls[0]?.Actions[0] as merchHtmlContent)?.HtmlContent;
  const HPEncart6BanniereContent = (data?.Zones.find(element => {
    return element.Name === 'HP_Encart6Banniere';
  })?.Controls[0]?.Actions[0] as merchHtmlContent)?.HtmlContent;
  const HPPush1BanniereContent = (data?.Zones.find(element => {
    return element.Name === 'HP_Push1Banniere';
  })?.Controls[0]?.Actions[0] as merchHtmlContent)?.HtmlContent;
  const HP_Push2BanniereContent = (data?.Zones.find(element => {
    return element.Name === 'HP_Push2Banniere';
  })?.Controls[0]?.Actions[0] as merchHtmlContent)?.HtmlContent;

  const HPPush1ProduitProducts = useMemo(() => {
    return getMerchActionProducts(
      data?.Zones.find(element => {
        return element.Name === 'HP_Push1Produit';
      })?.Controls[0]?.Actions as MerchActions,
      MAX_PRODUCTS_HPPush1Produit
    );
  }, [data?.Zones, MAX_PRODUCTS_HPPush1Produit]);

  const HPPush2ProduitProducts = useMemo(() => {
    return getMerchActionProducts(
      data?.Zones.find(element => {
        return element.Name === 'HP_Push2Produit';
      })?.Controls[0]?.Actions as MerchActions,
      MAX_PRODUCTS_HPPush2Produit
    );
  }, [data?.Zones, MAX_PRODUCTS_HPPush2Produit]);

  return (
    <div className={styles.root}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={
            metaData?.MetaDescription?.Value ||
            t('DEFAULT_HEAD_META_DESCRIPTION')
          }
        />
        <link rel={'canonical'} href={getFullUri('/')} />
      </Helmet>

      {HPEncart1BanniereContent && (
        <div
          dangerouslySetInnerHTML={{
            __html: replaceByLinkEnv(HPEncart1BanniereContent),
          }}
        />
      )}
      {HPEncart2BanniereContent && (
        <div
          dangerouslySetInnerHTML={{
            __html: replaceByLinkEnv(HPEncart2BanniereContent),
          }}
        />
      )}
      {HPEncart3BanniereContent && (
        <div
          dangerouslySetInnerHTML={{
            __html: replaceByLinkEnv(HPEncart3BanniereContent),
          }}
        />
      )}
      {HPEncart4BanniereContent && (
        <div
          dangerouslySetInnerHTML={{
            __html: replaceByLinkEnv(HPEncart4BanniereContent),
          }}
        />
      )}
      {HPEncart5BanniereContent && (
        <div
          dangerouslySetInnerHTML={{
            __html: replaceByLinkEnv(HPEncart5BanniereContent),
          }}
        />
      )}
      {HPEncart6BanniereContent && (
        <div
          dangerouslySetInnerHTML={{
            __html: replaceByLinkEnv(HPEncart6BanniereContent),
          }}
        />
      )}
      {HPPush1BanniereContent && (
        <div
          dangerouslySetInnerHTML={{
            __html: replaceByLinkEnv(HPPush1BanniereContent),
          }}
          className={styles.content}
        />
      )}
      <div className={styles.content}>
        <ErrorBoundary fallback={<></>}>
          <RenderCarousel
            pushProducts={HPPush1ProduitProducts}
            attributes={attributes}
          />
        </ErrorBoundary>
      </div>
      {HP_Push2BanniereContent && (
        <div
          dangerouslySetInnerHTML={{ __html: HP_Push2BanniereContent }}
          className={styles.content}
        />
      )}
      <div className={styles.content}>
        <ErrorBoundary fallback={<></>}>
          <RenderCarousel
            pushProducts={HPPush2ProduitProducts}
            attributes={attributes}
          />
        </ErrorBoundary>
      </div>
      <div className={styles.content}>
        <Footer />
      </div>
    </div>
  );
}

function RenderCarousel({
  pushProducts,
  attributes,
}: {
  pushProducts: ProductDetail[];
  attributes: FormattedFlowAttributeType;
}) {
  const formattedProductsPush = useMemo(() => {
    return getFormattedProductDetailToCarouselProduct(pushProducts, attributes);
  }, [pushProducts, attributes]);

  return (
    <CarouselProducts
      products={(formattedProductsPush as unknown) as CarouselProductType[]}
      productsDetails={pushProducts}
      attributes={attributes}
      gtmElementLabel="notre sélection page d'accueil"
      splideOptions={{
        mediaQuery: 'min',
        perPage: 2,
        breakpoints: {
          [DESKTOP_TABLET_MIN]: {
            perPage: 4,
            gap: '16px',
          },
        },
      }}
    />
  );
}
